import { useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import { FileBrowserMessage } from 'components/FileBrowser/FileBrowser';
import FileBrowserBar from 'components/FileBrowser/FileBrowserBar';
import ProviderFileBrowser from 'components/FileBrowser/ProviderFileBrowser';
import RootMessageFileBrowser from 'components/FileBrowser/RootMessageFileBrowser';
import {
  getAccountName,
  getIntegrationBanner,
  getIntegrationIcon,
  getIntegrationTextContent,
} from 'constants/integrations';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import React, { useEffect, useState } from 'react';
import { ModalState } from 'records/ModalState';
import {
  addProjectDriveAndSync,
  getProjectDriveMetaDataByConnectionId,
  syncProjectDrive,
  updateProjectDriveSourceTargetsAndSync,
} from 'services/projectDriveService';
import { truncateString } from 'utils/general';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import AddDMSConnectionModal from '../AddDMSConnectionModal';
import ConfirmModal from '../ConfirmModal';
import ModalStdHeader from '../ModalStdHeader';
import ManageConnectionIcon from './ManageConnectionIcon';
import ManageConnectionModal from './ManageConnectionModal';

interface Props extends WithAppStoreProps {
  accountLabel: string;
  connectionId: string;
  newConnection: boolean;
  providerType: string;
  isExpired: boolean;
}

const ManageConnectionModalFC: React.FC<Props> = ({
  accountLabel,
  actions,
  appStore,
  newConnection,
  providerType,
  connectionId,
  isExpired,
}) => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const { state } = appStore;
  const { integrationsSelectedItems, selectedProject } = state;

  const [loadingProjectDriveId, setLoadingProjectDrive] = useState(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [projectDriveId, setProjectDriveId] = useState<string | undefined>(
    undefined
  );
  const { accessToken } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [selectedItems, setSelectedItems] = useState(integrationsSelectedItems);
  const [recommendSync, setRecommendSync] = useState<boolean>(false);
  const [lastSync, setLastSync] = useState<string>();

  const { translate } = useTranslator(['integrationsPage', 'buttons']);

  const acct = getAccountName(providerType);
  const accountName =
    acct === 'Connection' ? translate('integrationsPage:default.title') : acct;

  const loadProjectDriveId = async () => {
    const drive = await getProjectDriveMetaDataByConnectionId(
      selectedProject,
      connectionId
    );
    setProjectDriveId(drive?.matchingDriveId);
    setLastSync(drive?.lastSync);
    setLoadingProjectDrive(false);
  };

  useEffect(() => {
    actions.setIntegrationConnectedAccountId(connectionId);
    loadProjectDriveId();
  }, []);

  useEffect(() => {
    setSelectedItems(integrationsSelectedItems);
  }, [integrationsSelectedItems]);

  const handleCancelClick = async (): Promise<void> => {
    const {
      clearAllModals,
      resetBreadcrumbs,
      resetIntegrationsView,
      resetIntegrationsSelectedItems,
    } = actions;
    await resetBreadcrumbs();
    await resetIntegrationsView();
    await resetIntegrationsSelectedItems();
    clearAllModals();
  };

  const handleReconnect = (): void => {
    const accessTokenParam = `?accessToken=${accessToken}`;
    const callback = `&callbackPath=/project/${state.selectedProject}/settings/integrations`;
    const projectId = `&projectId=${state.selectedProject}`;

    const icon = getIntegrationIcon(providerType);
    const banner = getIntegrationBanner(providerType);
    const content = getIntegrationTextContent(providerType);

    const authUrl = `${content.authUrl}${accessTokenParam}${callback}${projectId}`;

    const modalState = new ModalState({
      id: 'addDMSConnection',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        assets: {
          banner,
          icon,
        },
        getAuthUrl: async () => authUrl,
        content,
        actions: {
          cancelButton: actions.clearAllModals,
        },
      },
      component: AddDMSConnectionModal,
    });

    actions.replaceModal(modalState);
  };

  const handleManageConnection = (): void => {
    const modalState = new ModalState({
      className: 'modal__window modal--sm',
      id: 'launchManageConnectionModal',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        providerType,
        connectionId,
        accountLabel: truncateString(accountLabel, 40),
        isExpired,
        newConnection: false,
      },
      component: ManageConnectionModal,
    });

    actions.createModal(modalState);
  };

  const handleConfirmDisconnectAccount = async () => {
    await actions.disconnectAccount(connectionId, state.selectedProject);
    actions.clearAllModals();
  };

  const handleDisconnect = (evt: React.MouseEvent<HTMLElement>): void => {
    evt.stopPropagation();
    const modalState = new ModalState({
      id: 'launchDisconnectAccountDialog',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        headerText: translate(
          'integrationsPage:disconnectAccountDialog.headerText'
        ),
        acceptButtonText: translate('buttons:delete'),
        denyButtonText: translate('buttons:cancel'),
        acceptButtonType: 'delete',
        content: (
          <div className="column">
            <div className="bold pt1 mb3">
              {translate(
                'integrationsPage:disconnectAccountDialog.dialogQuestion'
              )}
            </div>
            <div>
              {translate('integrationsPage:disconnectAccountDialog.bodyText')}
            </div>
          </div>
        ),
        denyCallback: (): void => {
          handleManageConnection();
        },
        acceptCallback: (): void => {
          if (isPendoEnabled) {
            pendo.track('Integration disconnected');
          }
          handleConfirmDisconnectAccount();
        },
      },
      component: ConfirmModal,
    });

    actions.createModal(modalState);
  };

  const handleIntegrationSubmit = async (): Promise<void> => {
    const connectedAccount = state.getSelectedConnectedAccount();

    if (!connectedAccount) {
      return;
    }

    const driveSourceTargets = selectedItems
      .toList()
      .map(({ itemType, id, driveId, folderPathIds, siteId }) => ({
        itemType,
        itemId: id,
        driveId,
        folderPathIds: folderPathIds.toArray(),
        siteId,
      }))
      .toArray();

    if (projectDriveId) {
      updateProjectDriveSourceTargetsAndSync(
        selectedProject,
        projectDriveId,
        driveSourceTargets
      );
    } else {
      addProjectDriveAndSync(selectedProject, 'Bluebeam Drive', {
        connectedAccountId: connectedAccount.id,
        targets: driveSourceTargets,
      });
    }

    handleCancelClick();
  };

  const expiredMessage: FileBrowserMessage = {
    type: 'centered',
    value: {
      title: translate(
        'integrationsPage:manageConnectedAccount.filesUnavailable'
      ),
      body: translate(
        'integrationsPage:manageConnectedAccount.reconnectAccount'
      ),
    },
  };

  const loadingProjectDriveMessage: FileBrowserMessage = {
    type: 'loading',
    text: translate('integrationsPage:manageConnectedAccount.connectingTo', {
      accountName,
    }),
  };

  const syncDrive = async () => {
    if (projectDriveId) {
      setSyncing(true);
      setRecommendSync(false);
      await syncProjectDrive(projectDriveId, selectedProject);
      setSyncing(false);
    }
  };

  const handleDiscrepancyDetected = () => {
    setRecommendSync(true);
  };

  return (
    <div id="manage-connection-modal" className="modal__content oh">
      {newConnection ? (
        <ModalStdHeader text={accountName} onClose={handleCancelClick} />
      ) : (
        <ModalStdHeader
          text={`${translate(
            'integrationsPage:manageConnectedAccount.manage'
          )} ${accountName}`}
        />
      )}
      <div className="oa" style={{ maxHeight: '84vh' }}>
        <div className="px5 pt9 pb4 bb-3">
          {newConnection ? (
            <>
              <div className="font-size-7 black-2 medium">
                {translate('integrationsPage:manageConnectedAccount.subtitle')}
              </div>
              <div className="font-size-5 mt2 black-2 light">
                {translate(
                  'integrationsPage:manageConnectedAccount.instructions'
                )}
              </div>
            </>
          ) : (
            <div className="row justify-start items-start">
              <ManageConnectionIcon providerType={providerType} />
              <div className="column pl3">
                <div className="font-size-5 black-1">{accountName}</div>
                <div className="font-size-4 my2 black-2">{accountLabel}</div>
                {isExpired ? (
                  <span className="font-size-2" style={{ color: '#a71629' }}>
                    {translate(
                      'integrationsPage:manageConnectedAccount.expiredConnection'
                    )}
                  </span>
                ) : null}
              </div>
              {!isExpired ? (
                <button
                  id="integrations-modal-disconnect"
                  className="btn-sm btn-link"
                  style={{ color: '#a71629', marginLeft: 'auto' }}
                  onClick={handleDisconnect}
                >
                  {translate('buttons:disconnect')}
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div className="h100 px4 pt4 pb8">
          <div className="oh" style={{ height: '48vh', paddingBottom: '92px' }}>
            <FileBrowserBar
              selectedCount={selectedItems.count()}
              onSync={syncDrive}
              recommendSyncProviderType={
                recommendSync ? providerType : undefined
              }
              disableSync={syncing || !projectDriveId}
              searchInput={searchInput}
              onSearchChange={setSearchInput}
              lastSyncDate={lastSync}
            />
            {(() => {
              if (isExpired)
                return (
                  <RootMessageFileBrowser
                    providerType={providerType}
                    message={expiredMessage}
                  />
                );
              if (loadingProjectDriveId)
                return (
                  <RootMessageFileBrowser
                    providerType={providerType}
                    message={loadingProjectDriveMessage}
                  />
                );
              return (
                <ProviderFileBrowser
                  providerType={providerType}
                  connectionId={connectionId}
                  isExpired={isExpired}
                  filter={searchInput}
                  manageConnection={{
                    projectDriveId,
                    onDiscrepancyDetected: handleDiscrepancyDetected,
                  }}
                />
              );
            })()}
          </div>
          <div className="row px5 py3 so-vw100 w100 bgd-white-2 justify-between modal__button-row modal__button-row--with-border">
            <button
              id="integrations-modal-close"
              className="btn btn-link"
              onClick={handleCancelClick}
            >
              {newConnection
                ? translate('buttons:close')
                : translate('buttons:cancel')}
            </button>
            {isExpired ? (
              <button
                id="integrations-modal-reconnect"
                className="btn btn-primary"
                onClick={handleReconnect}
              >
                {translate('buttons:reconnect')}
              </button>
            ) : (
              <button
                data-analytics={
                  newConnection
                    ? 'integrations-share-items-button'
                    : 'integrations-update-items-button'
                }
                id="integrations-modal-submit"
                className="btn btn-primary"
                onClick={handleIntegrationSubmit}
                disabled={
                  selectedItems.isEmpty() || syncing || loadingProjectDriveId
                }
              >
                {newConnection
                  ? translate('buttons:share')
                  : translate('buttons:saveChanges')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAppStore<Props>(ManageConnectionModalFC);
